import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI, adminMembershipAPI, announcementAdminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn, formatDate } from '../lib/utils';
import {
    Users, Shield, ShieldOff, UserX, UserCheck, Search, BarChart3, BookOpen, Activity,
    RefreshCw, AlertTriangle, Ticket, Plus, Trash2, Copy, Check, CreditCard,
    Download, Library, Crown, ChevronRight, ArrowRight, Undo2, Loader2, CheckSquare, Square, GraduationCap, KeyRound,
    Bell, Megaphone, Info, AlertOctagon, XCircle, RotateCcw, Eye
} from 'lucide-react';
import Layout from '../components/Layout';

export default function Admin() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmDialog, setConfirmDialog] = useState({ open: false, user: null, action: null });
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [bulkActionLoading, setBulkActionLoading] = useState(false);

    // Bulk premium dialog
    const [bulkPremiumDialog, setBulkPremiumDialog] = useState(false);
    const [bulkPremiumDays, setBulkPremiumDays] = useState(365);

    // Bulk delete confirm
    const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);

    // Single user delete
    const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });

    // Premium dialog
    const [premiumDialog, setPremiumDialog] = useState({ open: false, user: null });
    const [premiumDays, setPremiumDays] = useState(365);

    // Reset password dialog
    const [resetDialog, setResetDialog] = useState({ open: false, user: null });
    const [newPassword, setNewPassword] = useState('');

    // Access codes state
    const [accessCodes, setAccessCodes] = useState([]);
    const [loadingCodes, setLoadingCodes] = useState(true);
    const [newCode, setNewCode] = useState('');
    const [newCodeDescription, setNewCodeDescription] = useState('');
    const [newCodeDuration, setNewCodeDuration] = useState(365);
    const [creatingCode, setCreatingCode] = useState(false);
    const [copiedCode, setCopiedCode] = useState(null);

    // Library management state
    const [libraryData, setLibraryData] = useState({ content: [], static_topics: { systems: {}, subjects: {} } });
    const [loadingLibrary, setLoadingLibrary] = useState(false);
    const [librarySearch, setLibrarySearch] = useState('');
    const [libraryTab, setLibraryTab] = useState('systems');
    const [reclassifyDialog, setReclassifyDialog] = useState({ open: false, topic: '', oldCategory: '', oldGroup: '' });
    const [reclassifyTarget, setReclassifyTarget] = useState({ category: 'systems', group: '' });

    // Announcement state
    const [announcements, setAnnouncements] = useState([]);
    const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
    const [showCreateAnnouncement, setShowCreateAnnouncement] = useState(false);
    const [creatingAnnouncement, setCreatingAnnouncement] = useState(false);
    const [announcementForm, setAnnouncementForm] = useState({
        title: '', body: '', priority: 'info', target: 'all', expires_at: '',
    });

    // Reference Books state
    const [referenceBooks, setReferenceBooks] = useState([]);
    const [loadingBooks, setLoadingBooks] = useState(false);
    const [uploadingBook, setUploadingBook] = useState(false);
    const [enrichmentStatus, setEnrichmentStatus] = useState(null);
    const [enriching, setEnriching] = useState(false);
    const [bookDiagnostics, setBookDiagnostics] = useState(null);

    useEffect(() => {
        if (user && !user.is_admin) {
            toast.error('Admin access required');
            navigate('/dashboard');
            return;
        }
        fetchData();
        fetchAccessCodes();
    }, [user, navigate]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [usersRes, statsRes] = await Promise.all([
                adminAPI.getUsers(0, 100),
                adminAPI.getStats()
            ]);
            setUsers(usersRes.data.users);
            setStats(statsRes.data);
        } catch (err) {
            if (err.response?.status === 403) {
                toast.error('Admin access required');
                navigate('/dashboard');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchAccessCodes = async () => {
        try {
            setLoadingCodes(true);
            const response = await adminMembershipAPI.listCodes();
            setAccessCodes(response.data.codes || []);
        } catch (err) {
            console.error('Failed to fetch access codes:', err);
        } finally {
            setLoadingCodes(false);
        }
    };

    const fetchLibrary = async () => {
        setLoadingLibrary(true);
        try {
            const res = await adminAPI.getLibraryContent();
            setLibraryData(res.data);
        } catch {
            toast.error('Failed to load library content');
        } finally {
            setLoadingLibrary(false);
        }
    };

    const fetchBooks = async () => {
        setLoadingBooks(true);
        try {
            const res = await adminAPI.getBooks();
            setReferenceBooks(res.data.books || []);
            // Also fetch diagnostics
            try {
                const diag = await adminAPI.getBookDiagnostics();
                setBookDiagnostics(diag.data);
            } catch { /* ignore */ }
        } catch {
            toast.error('Failed to load reference books');
        } finally {
            setLoadingBooks(false);
        }
    };

    const handleUploadBook = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.pdf')) {
            toast.error('Only PDF files are supported');
            return;
        }
        if (file.size > 400 * 1024 * 1024) {
            toast.error('File too large. Maximum 400MB.');
            return;
        }
        setUploadingBook(true);
        try {
            const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB chunks
            if (file.size > CHUNK_SIZE) {
                // Chunked upload for large files
                const initRes = await adminAPI.initBookUpload(file.name, file.size);
                const uploadId = initRes.data.upload_id;
                const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

                for (let i = 0; i < totalChunks; i++) {
                    const start = i * CHUNK_SIZE;
                    const end = Math.min(start + CHUNK_SIZE, file.size);
                    const chunk = file.slice(start, end);
                    const fd = new FormData();
                    fd.append('file', chunk, `chunk_${i}`);
                    await adminAPI.uploadBookChunk(uploadId, i, fd);
                    // Update progress toast
                    if (i % 5 === 0 || i === totalChunks - 1) {
                        toast.loading(`Uploading: ${Math.round(((i + 1) / totalChunks) * 100)}%`, { id: 'book-upload-progress' });
                    }
                }

                toast.loading('Processing book...', { id: 'book-upload-progress' });
                await adminAPI.completeBookUpload(uploadId);
                toast.success(`"${file.name}" uploaded successfully! All library articles will auto-regenerate with book references.`, { id: 'book-upload-progress', duration: 6000 });
            } else {
                // Small file — single request
                const formData = new FormData();
                formData.append('file', file);
                await adminAPI.uploadBook(formData);
                toast.success(`"${file.name}" uploaded successfully! All library articles will auto-regenerate with book references.`, { duration: 6000 });
            }
            fetchBooks();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to upload book', { id: 'book-upload-progress' });
        } finally {
            setUploadingBook(false);
            e.target.value = '';
        }
    };

    const handleDeleteBook = async (bookId, bookName) => {
        try {
            await adminAPI.deleteBook(bookId);
            toast.success(`"${bookName}" removed`);
            fetchBooks();
        } catch {
            toast.error('Failed to delete book');
        }
    };

    const handleEnrichLibrary = async () => {
        setEnriching(true);
        try {
            const res = await adminAPI.enrichLibrary();
            if (res.data.status === 'already_running') {
                toast.info('Enrichment is already in progress');
            } else {
                toast.success(`Enrichment started for ${res.data.total_topics} topics`);
            }
            pollEnrichmentStatus();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to start enrichment');
        } finally {
            setEnriching(false);
        }
    };

    const pollEnrichmentStatus = async () => {
        try {
            const res = await adminAPI.getEnrichmentStatus();
            setEnrichmentStatus(res.data.job || null);
            if (res.data.job?.status === 'running') {
                setTimeout(pollEnrichmentStatus, 5000);
            }
        } catch { /* ignore */ }
    };

    const handleCreateCode = async () => {
        if (!newCode.trim()) { toast.error('Please enter an access code'); return; }
        setCreatingCode(true);
        try {
            await adminMembershipAPI.createCode(newCode.trim(), newCodeDescription.trim(), newCodeDuration);
            toast.success('Access code created');
            setNewCode(''); setNewCodeDescription(''); setNewCodeDuration(365);
            fetchAccessCodes();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create code');
        } finally { setCreatingCode(false); }
    };

    const handleDeleteCode = async (code) => {
        try {
            await adminMembershipAPI.deleteCode(code);
            toast.success('Access code deactivated');
            fetchAccessCodes();
        } catch { toast.error('Failed to deactivate code'); }
    };

    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
        toast.success('Code copied');
    };

    const handleSuspend = async (targetUser, suspend) => {
        try {
            await adminAPI.suspendUser(targetUser.id, suspend);
            toast.success(suspend ? 'User suspended' : 'User unsuspended');
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
        setConfirmDialog({ open: false, user: null, action: null });
    };

    const handleToggleProfessor = async (targetUser) => {
        try {
            await adminAPI.setProfessor(targetUser.id, !targetUser.is_professor);
            toast.success(targetUser.is_professor ? 'Professor role revoked' : 'Professor role granted');
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    };

    const handleResetPassword = async () => {
        if (!resetDialog.user || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        try {
            await adminAPI.resetPassword(resetDialog.user.id, newPassword);
            toast.success(`Password reset for ${resetDialog.user.email}`);
            setResetDialog({ open: false, user: null });
            setNewPassword('');
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    };

    const handleBulkSuspend = async (suspend) => {
        setBulkActionLoading(true);
        try {
            const ids = [...selectedUsers];
            if (suspend) {
                await adminAPI.bulkSuspend(ids);
            } else {
                await adminAPI.bulkUnsuspend(ids);
            }
            toast.success(`${ids.length} user(s) ${suspend ? 'suspended' : 'unsuspended'}`);
            setSelectedUsers(new Set());
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Bulk action failed'); }
        finally { setBulkActionLoading(false); }
    };

    const handleBulkSetPremium = async () => {
        setBulkActionLoading(true);
        try {
            const ids = [...selectedUsers];
            await adminAPI.bulkSetPremium(ids, bulkPremiumDays);
            toast.success(`Premium granted to ${ids.length} user(s) for ${bulkPremiumDays} days`);
            setSelectedUsers(new Set());
            setBulkPremiumDialog(false);
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Bulk premium failed'); }
        finally { setBulkActionLoading(false); }
    };

    const handleBulkSetProfessor = async (isProfessor) => {
        setBulkActionLoading(true);
        try {
            const ids = [...selectedUsers];
            await adminAPI.bulkSetProfessor(ids, isProfessor);
            toast.success(`${ids.length} user(s) ${isProfessor ? 'granted' : 'revoked'} professor status`);
            setSelectedUsers(new Set());
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Bulk professor failed'); }
        finally { setBulkActionLoading(false); }
    };

    const handleBulkDelete = async () => {
        setBulkActionLoading(true);
        try {
            const ids = [...selectedUsers];
            await adminAPI.bulkDelete(ids);
            toast.success(`${ids.length} user(s) deleted`);
            setSelectedUsers(new Set());
            setBulkDeleteDialog(false);
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Bulk delete failed'); }
        finally { setBulkActionLoading(false); }
    };

    const handleDeleteUser = async () => {
        try {
            await adminAPI.deleteUser(deleteDialog.user.id);
            toast.success(`${deleteDialog.user.email} deleted`);
            setDeleteDialog({ open: false, user: null });
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
    };

    const toggleSelectAll = () => {
        if (selectedUsers.size === filteredUsers.filter(u => !u.is_admin && u.id !== user.id).length) {
            setSelectedUsers(new Set());
        } else {
            setSelectedUsers(new Set(filteredUsers.filter(u => !u.is_admin && u.id !== user.id).map(u => u.id)));
        }
    };

    const handleSetPremium = async () => {
        if (!premiumDialog.user) return;
        try {
            await adminAPI.setPremium(premiumDialog.user.id, premiumDays);
            toast.success(premiumDays > 0 ? `Premium granted for ${premiumDays} days` : 'Premium revoked');
            setPremiumDialog({ open: false, user: null });
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    };

    const handleExportCsv = async () => {
        try {
            const res = await adminAPI.exportUsersCsv();
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url; a.download = 'medprep_users.csv';
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('CSV exported');
        } catch { toast.error('Export failed'); }
    };

    const handleDeleteLibrary = async (category, groupName, topic = null) => {
        try {
            await adminAPI.deleteLibraryContent(category, groupName, topic);
            toast.success(topic ? `"${topic}" removed` : `"${groupName}" removed`);
            fetchLibrary();
        } catch { toast.error('Failed to delete'); }
    };

    const handleRestoreLibrary = async (category, groupName, topic = null) => {
        try {
            await adminAPI.restoreLibraryTopic(category, groupName, topic);
            toast.success('Restored');
            fetchLibrary();
        } catch { toast.error('Failed to restore'); }
    };

    const handleReclassify = async () => {
        const { topic, oldCategory, oldGroup } = reclassifyDialog;
        const { category, group } = reclassifyTarget;
        if (!group) { toast.error('Select a target group'); return; }
        try {
            await adminAPI.reclassifyLibraryTopic(topic, oldCategory, oldGroup, category, group);
            toast.success(`"${topic}" moved to ${group}`);
            setReclassifyDialog({ open: false, topic: '', oldCategory: '', oldGroup: '' });
            fetchLibrary();
        } catch { toast.error('Failed to reclassify'); }
    };

    // ── Announcement functions ──
    const fetchAnnouncements = async () => {
        setLoadingAnnouncements(true);
        try {
            const res = await announcementAdminAPI.list();
            setAnnouncements(res.data.announcements || []);
        } catch { toast.error('Failed to load announcements'); }
        finally { setLoadingAnnouncements(false); }
    };

    const handleCreateAnnouncement = async () => {
        if (!announcementForm.title.trim()) { toast.error('Enter a title'); return; }
        if (!announcementForm.body.trim()) { toast.error('Enter a message body'); return; }
        setCreatingAnnouncement(true);
        try {
            await announcementAdminAPI.create({
                ...announcementForm,
                expires_at: announcementForm.expires_at || null,
            });
            toast.success('Announcement created');
            setShowCreateAnnouncement(false);
            setAnnouncementForm({ title: '', body: '', priority: 'info', target: 'all', expires_at: '' });
            fetchAnnouncements();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create announcement');
        } finally { setCreatingAnnouncement(false); }
    };

    const handleCancelAnnouncement = async (id) => {
        try {
            await announcementAdminAPI.cancel(id);
            toast.success('Announcement cancelled');
            fetchAnnouncements();
        } catch { toast.error('Failed to cancel'); }
    };

    const handleReactivateAnnouncement = async (id) => {
        try {
            await announcementAdminAPI.reactivate(id);
            toast.success('Announcement reactivated');
            fetchAnnouncements();
        } catch { toast.error('Failed to reactivate'); }
    };

    const handleDeleteAnnouncement = async (id) => {
        try {
            await announcementAdminAPI.delete(id);
            toast.success('Announcement deleted');
            fetchAnnouncements();
        } catch { toast.error('Failed to delete'); }
    };

    const toggleUserSelect = (uid) => {
        setSelectedUsers(prev => {
            const next = new Set(prev);
            if (next.has(uid)) next.delete(uid); else next.add(uid);
            return next;
        });
    };

    const filteredUsers = users.filter(u =>
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getMembershipBadge = (u) => {
        if (u.membership_expires) {
            const exp = new Date(u.membership_expires);
            if (exp > new Date()) {
                return <Badge className="bg-emerald-100 text-emerald-700"><Crown className="h-3 w-3 mr-1" />Premium</Badge>;
            }
            return <Badge className="bg-red-100 text-red-700">Expired</Badge>;
        }
        return <Badge className="bg-slate-100 text-slate-600">Free Trial</Badge>;
    };

    // Build library view with static topics
    const buildLibraryView = () => {
        const key = libraryTab === 'systems' ? 'systems' : 'subjects';
        const staticTopics = libraryData.static_topics[key] || {};
        const q = librarySearch.toLowerCase();

        return Object.entries(staticTopics)
            .filter(([group, topics]) =>
                !q || group.toLowerCase().includes(q) || topics.some(t => t.toLowerCase().includes(q))
            )
            .map(([group, topics]) => ({
                group,
                topics: topics.filter(t => !q || t.toLowerCase().includes(q) || group.toLowerCase().includes(q))
            }));
    };

    if (!user?.is_admin) return null;

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
                    </div>
                    <Skeleton className="h-96" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="admin-container">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                            Administrator Dashboard
                        </h1>
                        <p className="text-slate-500 mt-1">Manage users, content, and platform activity</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={handleExportCsv} data-testid="admin-export-csv">
                            <Download className="mr-2 h-4 w-4" />
                            Export CSV
                        </Button>
                        <Button variant="outline" size="sm" onClick={fetchData} data-testid="admin-refresh-btn">
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card className="border-slate-200">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-slate-500">Total Users</p>
                                        <p className="text-2xl font-bold text-slate-900">{stats.total_users}</p>
                                    </div>
                                    <Users className="h-8 w-8 text-indigo-500" />
                                </div>
                                <div className="mt-2 flex gap-2 text-xs">
                                    <span className="text-emerald-600">{stats.active_users} active</span>
                                    <span className="text-red-600">{stats.suspended_users} suspended</span>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-slate-200">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-slate-500">Questions Generated</p>
                                        <p className="text-2xl font-bold text-slate-900">{stats.total_questions_generated}</p>
                                    </div>
                                    <BookOpen className="h-8 w-8 text-emerald-500" />
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-slate-200">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-slate-500">Total Attempts</p>
                                        <p className="text-2xl font-bold text-slate-900">{stats.total_attempts}</p>
                                    </div>
                                    <Activity className="h-8 w-8 text-amber-500" />
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-slate-200">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-slate-500">Access Codes</p>
                                        <p className="text-2xl font-bold text-slate-900">{accessCodes.length}</p>
                                    </div>
                                    <Ticket className="h-8 w-8 text-purple-500" />
                                </div>
                                <div className="mt-2 flex gap-2 text-xs">
                                    <span className="text-emerald-600">{accessCodes.filter(c => !c.redeemed_by && c.is_active).length} available</span>
                                    <span className="text-slate-500">{accessCodes.filter(c => c.redeemed_by).length} redeemed</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Main Tabs */}
                <Tabs defaultValue="users" className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="users" className="flex items-center gap-2"><Users className="h-4 w-4" />Users</TabsTrigger>
                        <TabsTrigger value="announcements" className="flex items-center gap-2" onClick={() => { if (announcements.length === 0) fetchAnnouncements(); }}>
                            <Megaphone className="h-4 w-4" />Announcements
                        </TabsTrigger>
                        <TabsTrigger value="access-codes" className="flex items-center gap-2"><Ticket className="h-4 w-4" />Access Codes</TabsTrigger>
                        <TabsTrigger value="library" className="flex items-center gap-2" onClick={() => { if (!libraryData.static_topics.systems?.Cardiovascular) fetchLibrary(); fetchBooks(); pollEnrichmentStatus(); }}>
                            <Library className="h-4 w-4" />Library
                        </TabsTrigger>
                    </TabsList>

                    {/* ==================== USERS TAB ==================== */}
                    <TabsContent value="users">
                        <Card className="border-slate-200">
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <CardTitle style={{ fontFamily: 'Manrope' }}>User Management</CardTitle>
                                        <CardDescription>View, manage, and set premium status</CardDescription>
                                    </div>
                                    <div className="flex gap-2 items-center flex-wrap">
                                        {selectedUsers.size > 0 && (
                                            <div className="flex gap-1.5 flex-wrap">
                                                <Badge variant="secondary" className="text-xs">{selectedUsers.size} selected</Badge>
                                                <Button size="sm" variant="destructive" onClick={() => handleBulkSuspend(true)} disabled={bulkActionLoading} data-testid="admin-bulk-suspend">
                                                    <ShieldOff className="h-3 w-3 mr-1" />Suspend
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => handleBulkSuspend(false)} disabled={bulkActionLoading} data-testid="admin-bulk-unsuspend">
                                                    <UserCheck className="h-3 w-3 mr-1" />Unsuspend
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => setBulkPremiumDialog(true)} disabled={bulkActionLoading} data-testid="admin-bulk-premium">
                                                    <Crown className="h-3 w-3 mr-1" />Premium
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => handleBulkSetProfessor(true)} disabled={bulkActionLoading} data-testid="admin-bulk-professor">
                                                    <GraduationCap className="h-3 w-3 mr-1" />Professor
                                                </Button>
                                                <Button size="sm" variant="destructive" className="bg-red-700 hover:bg-red-800" onClick={() => setBulkDeleteDialog(true)} disabled={bulkActionLoading} data-testid="admin-bulk-delete">
                                                    <Trash2 className="h-3 w-3 mr-1" />Delete
                                                </Button>
                                            </div>
                                        )}
                                        <div className="relative w-full sm:w-64">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                            <Input placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="admin-search-input" />
                                        </div>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="rounded-lg border border-slate-200 overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-slate-50">
                                                <TableHead className="w-10">
                                                    <button onClick={toggleSelectAll} data-testid="admin-select-all">
                                                        {selectedUsers.size > 0 && selectedUsers.size === filteredUsers.filter(u => !u.is_admin && u.id !== user.id).length
                                                            ? <CheckSquare className="h-4 w-4 text-indigo-500" />
                                                            : <Square className="h-4 w-4 text-slate-300" />}
                                                    </button>
                                                </TableHead>
                                                <TableHead>User</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Membership</TableHead>
                                                <TableHead className="hidden md:table-cell">Questions</TableHead>
                                                <TableHead className="hidden sm:table-cell">Joined</TableHead>
                                                <TableHead className="text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {filteredUsers.map((u) => (
                                                <TableRow key={u.id} className={cn(u.is_suspended && "bg-red-50/50", selectedUsers.has(u.id) && "bg-indigo-50/50")}>
                                                    <TableCell>
                                                        {!u.is_admin && u.id !== user.id && (
                                                            <button onClick={() => toggleUserSelect(u.id)} data-testid={`admin-select-${u.id}`}>
                                                                {selectedUsers.has(u.id) ? <CheckSquare className="h-4 w-4 text-indigo-500" /> : <Square className="h-4 w-4 text-slate-300" />}
                                                            </button>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <p className="font-medium text-slate-900">{u.name}</p>
                                                        <p className="text-sm text-slate-500">{u.email}</p>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex flex-wrap gap-1">
                                                            {u.is_admin && <Badge className="bg-indigo-100 text-indigo-700"><Shield className="h-3 w-3 mr-1" />Admin</Badge>}
                                                            {u.is_professor && <Badge className="bg-purple-100 text-purple-700"><GraduationCap className="h-3 w-3 mr-1" />Professor</Badge>}
                                                            {u.is_suspended ? (
                                                                <Badge variant="destructive"><UserX className="h-3 w-3 mr-1" />Suspended</Badge>
                                                            ) : (
                                                                <Badge className="bg-emerald-100 text-emerald-700"><UserCheck className="h-3 w-3 mr-1" />Active</Badge>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>{getMembershipBadge(u)}</TableCell>
                                                    <TableCell className="hidden md:table-cell">{u.total_questions}</TableCell>
                                                    <TableCell className="hidden sm:table-cell text-sm text-slate-500">{formatDate(u.created_at)}</TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex gap-1 justify-end">
                                                            {!u.is_admin && u.id !== user.id && (
                                                                <>
                                                                    <Button variant={u.is_professor ? "default" : "outline"} size="sm"
                                                                        onClick={() => handleToggleProfessor(u)}
                                                                        className={u.is_professor ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}
                                                                        data-testid={`admin-professor-${u.id}`}>
                                                                        <GraduationCap className="h-3 w-3 mr-1" />{u.is_professor ? 'Professor' : 'Set Prof'}
                                                                    </Button>
                                                                    <Button variant="outline" size="sm" onClick={() => { setPremiumDialog({ open: true, user: u }); setPremiumDays(365); }} data-testid={`admin-premium-${u.id}`}>
                                                                        <Crown className="h-3 w-3 mr-1" />Premium
                                                                    </Button>
                                                                    <Button variant={u.is_suspended ? "outline" : "destructive"} size="sm"
                                                                        onClick={() => setConfirmDialog({ open: true, user: u, action: u.is_suspended ? 'unsuspend' : 'suspend' })}
                                                                        data-testid={`admin-suspend-${u.id}`}>
                                                                        {u.is_suspended ? <><UserCheck className="h-3 w-3 mr-1" />Unsuspend</> : <><ShieldOff className="h-3 w-3 mr-1" />Suspend</>}
                                                                    </Button>
                                                                    <Button variant="outline" size="sm"
                                                                        onClick={() => { setResetDialog({ open: true, user: u }); setNewPassword(''); }}
                                                                        data-testid={`admin-reset-pwd-${u.id}`}>
                                                                        <KeyRound className="h-3 w-3 mr-1" />Reset Pwd
                                                                    </Button>
                                                                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                        onClick={() => setDeleteDialog({ open: true, user: u })}
                                                                        data-testid={`admin-delete-${u.id}`}>
                                                                        <Trash2 className="h-3 w-3" />
                                                                    </Button>
                                                                </>
                                                            )}
                                                            {u.id === user.id && <span className="text-sm text-slate-400">You</span>}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {filteredUsers.length === 0 && (
                                                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No users found</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* ==================== ANNOUNCEMENTS TAB ==================== */}
                    <TabsContent value="announcements">
                        <Card className="border-slate-200">
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <CardTitle style={{ fontFamily: 'Manrope' }}>Announcements</CardTitle>
                                        <CardDescription>Send messages to users that appear on their next login</CardDescription>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="outline" size="sm" onClick={fetchAnnouncements}>
                                            <RefreshCw className={cn("h-4 w-4 mr-1", loadingAnnouncements && "animate-spin")} />Refresh
                                        </Button>
                                        <Button size="sm" onClick={() => setShowCreateAnnouncement(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="admin-create-announcement">
                                            <Plus className="h-4 w-4 mr-1" />New Announcement
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loadingAnnouncements ? (
                                    <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
                                ) : announcements.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        <Megaphone className="h-12 w-12 mx-auto mb-3 opacity-40" />
                                        <p>No announcements yet. Create one to notify your users.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {announcements.map(a => {
                                            const isExpired = a.expires_at && new Date(a.expires_at) < new Date();
                                            const priorityColors = {
                                                info: 'bg-blue-100 text-blue-700',
                                                important: 'bg-amber-100 text-amber-700',
                                                critical: 'bg-red-100 text-red-700',
                                            };
                                            const targetLabels = { all: 'All Users', professors: 'Professors', students: 'Students' };
                                            return (
                                                <div key={a.id} className={cn("rounded-lg border p-4", !a.is_active || isExpired ? "bg-slate-50 border-slate-200 opacity-60" : "border-slate-200")} data-testid={`announcement-row-${a.id}`}>
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                                <h4 className="font-semibold text-slate-900">{a.title}</h4>
                                                                <Badge className={cn("text-xs", priorityColors[a.priority])}>{a.priority}</Badge>
                                                                <Badge variant="outline" className="text-xs">{targetLabels[a.target] || a.target}</Badge>
                                                                {!a.is_active && <Badge className="bg-slate-200 text-slate-600 text-xs">Cancelled</Badge>}
                                                                {isExpired && a.is_active && <Badge className="bg-orange-100 text-orange-700 text-xs">Expired</Badge>}
                                                                {a.is_active && !isExpired && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Active</Badge>}
                                                            </div>
                                                            <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-2">{a.body}</p>
                                                            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                                                                <span>Created {new Date(a.created_at).toLocaleDateString()}</span>
                                                                {a.expires_at && <span>Expires {new Date(a.expires_at).toLocaleDateString()}</span>}
                                                                <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{a.read_count || 0} read</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-1 shrink-0">
                                                            {a.is_active && !isExpired ? (
                                                                <Button variant="outline" size="sm" onClick={() => handleCancelAnnouncement(a.id)} data-testid={`cancel-announcement-${a.id}`} title="Cancel">
                                                                    <XCircle className="h-4 w-4 text-amber-500" />
                                                                </Button>
                                                            ) : !isExpired ? (
                                                                <Button variant="outline" size="sm" onClick={() => handleReactivateAnnouncement(a.id)} data-testid={`reactivate-announcement-${a.id}`} title="Reactivate">
                                                                    <RotateCcw className="h-4 w-4 text-emerald-500" />
                                                                </Button>
                                                            ) : null}
                                                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteAnnouncement(a.id)} data-testid={`delete-announcement-${a.id}`} title="Delete">
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* ==================== ACCESS CODES TAB ==================== */}
                    <TabsContent value="access-codes">
                        <Card className="border-slate-200">
                            <CardHeader>
                                <CardTitle style={{ fontFamily: 'Manrope' }}>Access Code Management</CardTitle>
                                <CardDescription>Create and manage access codes for premium features</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                                    <h3 className="font-medium text-slate-900 mb-3">Create New Access Code</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div>
                                            <Label htmlFor="new-code">Access Code</Label>
                                            <Input id="new-code" placeholder="Enter code" value={newCode} onChange={(e) => setNewCode(e.target.value)} />
                                        </div>
                                        <div>
                                            <Label htmlFor="new-code-desc">Description</Label>
                                            <Input id="new-code-desc" placeholder="Optional" value={newCodeDescription} onChange={(e) => setNewCodeDescription(e.target.value)} />
                                        </div>
                                        <div>
                                            <Label htmlFor="new-code-duration">Duration</Label>
                                            <select id="new-code-duration" value={newCodeDuration} onChange={(e) => setNewCodeDuration(Number(e.target.value))}
                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                                                <option value={30}>1 Month</option>
                                                <option value={90}>3 Months</option>
                                                <option value={180}>6 Months</option>
                                                <option value={365}>1 Year</option>
                                                <option value={730}>2 Years</option>
                                            </select>
                                        </div>
                                        <div className="flex items-end">
                                            <Button onClick={handleCreateCode} disabled={creatingCode || !newCode.trim()} className="w-full">
                                                {creatingCode ? <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Creating...</> : <><Plus className="mr-2 h-4 w-4" />Create Code</>}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                                <div className="rounded-lg border border-slate-200 overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-slate-50">
                                                <TableHead>Code</TableHead>
                                                <TableHead>Description</TableHead>
                                                <TableHead>Duration</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead className="hidden md:table-cell">Redeemed By</TableHead>
                                                <TableHead className="hidden sm:table-cell">Created</TableHead>
                                                <TableHead className="text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {loadingCodes ? (
                                                <TableRow><TableCell colSpan={7} className="text-center py-8"><RefreshCw className="h-4 w-4 animate-spin mx-auto" /></TableCell></TableRow>
                                            ) : accessCodes.length === 0 ? (
                                                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No access codes found</TableCell></TableRow>
                                            ) : accessCodes.map((code) => (
                                                <TableRow key={code.code} className={cn(!code.is_active && "bg-red-50/50")}>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">{code.code}</code>
                                                            <Button variant="ghost" size="sm" onClick={() => copyCode(code.code)} className="h-6 w-6 p-0">
                                                                {copiedCode === code.code ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell><span className="text-sm text-slate-600">{code.description || 'No description'}</span></TableCell>
                                                    <TableCell><span className="text-sm">{code.duration_days >= 365 ? `${Math.floor(code.duration_days / 365)} year${Math.floor(code.duration_days / 365) > 1 ? 's' : ''}` : code.duration_days >= 30 ? `${Math.floor(code.duration_days / 30)} month${Math.floor(code.duration_days / 30) > 1 ? 's' : ''}` : `${code.duration_days} days`}</span></TableCell>
                                                    <TableCell>
                                                        {code.redeemed_by ? <Badge className="bg-emerald-100 text-emerald-700"><CreditCard className="h-3 w-3 mr-1" />Redeemed</Badge>
                                                            : code.is_active ? <Badge className="bg-blue-100 text-blue-700"><Ticket className="h-3 w-3 mr-1" />Available</Badge>
                                                            : <Badge variant="destructive"><UserX className="h-3 w-3 mr-1" />Inactive</Badge>}
                                                    </TableCell>
                                                    <TableCell className="hidden md:table-cell"><span className="text-sm text-slate-600">{code.redeemed_by || 'Not redeemed'}</span></TableCell>
                                                    <TableCell className="hidden sm:table-cell text-sm text-slate-500">{formatDate(code.created_at)}</TableCell>
                                                    <TableCell className="text-right">
                                                        {code.is_active && !code.redeemed_by && (
                                                            <Button variant="destructive" size="sm" onClick={() => handleDeleteCode(code.code)}>
                                                                <Trash2 className="h-4 w-4 mr-1" />Deactivate
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* ==================== LIBRARY TAB ==================== */}
                    <TabsContent value="library">
                        {/* Reference Books Section */}
                        <Card className="border-slate-200 mb-4">
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <CardTitle style={{ fontFamily: 'Manrope' }}>Reference Books</CardTitle>
                                        <CardDescription>Upload PDF books to enrich library content with cross-references</CardDescription>
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        <label htmlFor="book-upload" data-testid="upload-book-btn">
                                            <Button variant="default" size="sm" asChild disabled={uploadingBook}>
                                                <span className="cursor-pointer">
                                                    {uploadingBook ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                                                    {uploadingBook ? 'Processing...' : 'Upload Book'}
                                                </span>
                                            </Button>
                                        </label>
                                        <input id="book-upload" type="file" accept=".pdf" className="hidden" onChange={handleUploadBook} disabled={uploadingBook} />
                                        <Button variant="outline" size="sm" onClick={() => { fetchBooks(); pollEnrichmentStatus(); }} data-testid="refresh-books-btn">
                                            <RefreshCw className={cn("h-4 w-4", loadingBooks && "animate-spin")} />
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {referenceBooks.length === 0 && !loadingBooks ? (
                                    <p className="text-center py-6 text-slate-500">No reference books uploaded yet. Upload a PDF to enrich library content.</p>
                                ) : loadingBooks ? (
                                    <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
                                ) : (
                                    <div className="space-y-3">
                                        {referenceBooks.map(book => (
                                            <div key={book.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50" data-testid={`book-${book.id}`}>
                                                <div className="flex items-center gap-3">
                                                    <BookOpen className="h-5 w-5 text-indigo-500 shrink-0" />
                                                    <div>
                                                        <p className="text-sm font-medium text-slate-800">{book.name}</p>
                                                        <div className="flex gap-3 text-xs text-slate-500 mt-0.5">
                                                            <span>v{book.version || 1}</span>
                                                            <span>{book.total_chunks || 0} chunks</span>
                                                            <span>{(book.file_size / 1024 / 1024).toFixed(1)} MB</span>
                                                            <span>{book.uploaded_at ? formatDate(book.uploaded_at) : ''}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Badge variant={book.status === 'ready' ? 'default' : 'secondary'}>{book.status}</Badge>
                                                    {book.auto_gen_status && (
                                                        <Badge variant="outline" className="text-xs">
                                                            {book.auto_gen_status === 'completed'
                                                                ? `${book.auto_gen_completed || 0} articles`
                                                                : book.auto_gen_status === 'generating'
                                                                ? `Generating ${book.auto_gen_completed || 0}/${book.auto_gen_total || '?'}`
                                                                : book.auto_gen_status}
                                                        </Badge>
                                                    )}
                                                    {book.status === 'ready' && (!book.auto_gen_status || book.auto_gen_status === 'completed') && (
                                                        <Button variant="outline" size="sm" className="h-7 text-xs"
                                                            onClick={async () => {
                                                                try {
                                                                    toast.info(`Starting article generation from "${book.name}"...`);
                                                                    await adminAPI.autoGenerateFromBook(book.id);
                                                                    toast.success('Auto-generation started! Articles will appear in the library as they are created.');
                                                                    setTimeout(fetchBooks, 5000);
                                                                } catch (e) { toast.error('Failed to start auto-generation'); }
                                                            }}
                                                            data-testid={`auto-gen-${book.id}`}>
                                                            Generate Articles
                                                        </Button>
                                                    )}
                                                    <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => handleDeleteBook(book.id, book.name)}
                                                        data-testid={`delete-book-${book.id}`}>
                                                        <Trash2 className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {referenceBooks.length > 0 && (
                                    <div className="mt-4 pt-4 border-t border-slate-200">
                                        {/* Diagnostics Summary */}
                                        {bookDiagnostics && (
                                            <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="book-diagnostics">
                                                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Book Health</p>
                                                <div className="grid grid-cols-3 gap-3 text-sm">
                                                    <div>
                                                        <span className="text-slate-500">Total Chunks:</span>{' '}
                                                        <span className="font-medium">{bookDiagnostics.books?.reduce((s, b) => s + b.total_chunks, 0) || 0}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500">Library Articles:</span>{' '}
                                                        <span className="font-medium">{bookDiagnostics.library_articles_total || 0}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500">With Book Refs:</span>{' '}
                                                        <span className="font-medium text-sky-600">{bookDiagnostics.library_articles_with_book_refs || 0}</span>
                                                    </div>
                                                </div>
                                                {bookDiagnostics.books?.map(b => (
                                                    <div key={b.book_id} className="mt-2 text-xs text-slate-500">
                                                        <span className="font-medium text-slate-700">{b.name}</span>: {b.total_chunks} chunks, {b.text_length?.toLocaleString()} chars
                                                        {b.sample_chunks?.[0] && (
                                                            <span className="ml-1 text-slate-400">— Preview: "{b.sample_chunks[0].text_preview?.slice(0, 80)}..."</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium text-slate-700">Enrich All Library Topics</p>
                                                <p className="text-xs text-slate-500">Generate cross-referenced content for all topics using uploaded books</p>
                                            </div>
                                            <Button onClick={handleEnrichLibrary} disabled={enriching || enrichmentStatus?.status === 'running'} size="sm" data-testid="enrich-library-btn">
                                                {enriching || enrichmentStatus?.status === 'running' ? (
                                                    <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Enriching...</>
                                                ) : (
                                                    <><RefreshCw className="h-4 w-4 mr-1" />Enrich All</>
                                                )}
                                            </Button>
                                        </div>
                                        {enrichmentStatus && enrichmentStatus.status === 'running' && (
                                            <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                                                <div className="flex items-center justify-between text-sm text-indigo-700">
                                                    <span>Processing: {enrichmentStatus.processed}/{enrichmentStatus.total_topics} topics</span>
                                                    <span>{enrichmentStatus.enriched} enriched, {enrichmentStatus.errors} errors</span>
                                                </div>
                                                <div className="mt-2 w-full bg-indigo-100 rounded-full h-2">
                                                    <div className="bg-indigo-500 h-2 rounded-full transition-all" style={{ width: `${enrichmentStatus.total_topics > 0 ? (enrichmentStatus.processed / enrichmentStatus.total_topics * 100) : 0}%` }} />
                                                </div>
                                            </div>
                                        )}
                                        {enrichmentStatus && enrichmentStatus.status === 'completed' && (
                                            <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
                                                Enrichment complete: {enrichmentStatus.enriched} topics enriched, {enrichmentStatus.errors} errors
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="border-slate-200">
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <CardTitle style={{ fontFamily: 'Manrope' }}>Library Content Management</CardTitle>
                                        <CardDescription>Remove, reclassify, or restore library topics</CardDescription>
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        <div className="flex border rounded-lg overflow-hidden">
                                            <button onClick={() => setLibraryTab('systems')} className={cn("px-3 py-1.5 text-sm font-medium", libraryTab === 'systems' ? "bg-indigo-100 text-indigo-700" : "text-slate-500 hover:bg-slate-50")}>By System</button>
                                            <button onClick={() => setLibraryTab('subjects')} className={cn("px-3 py-1.5 text-sm font-medium", libraryTab === 'subjects' ? "bg-indigo-100 text-indigo-700" : "text-slate-500 hover:bg-slate-50")}>By Subject</button>
                                        </div>
                                        <div className="relative w-48">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                            <Input placeholder="Search..." value={librarySearch} onChange={(e) => setLibrarySearch(e.target.value)} className="pl-9" data-testid="admin-library-search" />
                                        </div>
                                        <Button variant="outline" size="sm" onClick={fetchLibrary} data-testid="admin-library-refresh">
                                            <RefreshCw className={cn("h-4 w-4", loadingLibrary && "animate-spin")} />
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loadingLibrary ? (
                                    <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
                                ) : (
                                    <div className="space-y-4">
                                        {buildLibraryView().map(({ group, topics }) => (
                                            <div key={group} className="border border-slate-200 rounded-lg overflow-hidden">
                                                <div className="flex items-center justify-between px-4 py-3 bg-slate-50">
                                                    <h3 className="font-semibold text-slate-800">{group} <span className="text-xs font-normal text-slate-400 ml-1">({topics.length} topics)</span></h3>
                                                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => handleDeleteLibrary(libraryTab, group)}
                                                        data-testid={`admin-delete-group-${group.replace(/[\s/]+/g, '-').toLowerCase()}`}>
                                                        <Trash2 className="h-3 w-3 mr-1" />Remove All
                                                    </Button>
                                                </div>
                                                <div className="divide-y divide-slate-100">
                                                    {topics.map(topic => (
                                                        <div key={topic} className="flex items-center justify-between px-4 py-2 hover:bg-slate-50 group">
                                                            <span className="text-sm text-slate-700">{topic}</span>
                                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <Button variant="ghost" size="sm" className="h-7 text-xs text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                                                                    onClick={() => { setReclassifyDialog({ open: true, topic, oldCategory: libraryTab, oldGroup: group }); setReclassifyTarget({ category: libraryTab, group: '' }); }}
                                                                    data-testid={`admin-reclassify-${topic.replace(/[\s/]+/g, '-').toLowerCase()}`}>
                                                                    <ArrowRight className="h-3 w-3 mr-1" />Move
                                                                </Button>
                                                                <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                                                                    onClick={() => handleDeleteLibrary(libraryTab, group, topic)}
                                                                    data-testid={`admin-delete-topic-${topic.replace(/[\s/]+/g, '-').toLowerCase()}`}>
                                                                    <Trash2 className="h-3 w-3" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                        {buildLibraryView().length === 0 && (
                                            <p className="text-center py-8 text-slate-500">{librarySearch ? 'No matching topics' : 'No library content. Click Refresh to load.'}</p>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                {/* Create Announcement Dialog */}
                <Dialog open={showCreateAnnouncement} onOpenChange={(o) => { if (!o) setShowCreateAnnouncement(false); }}>
                    <DialogContent className="sm:max-w-lg" data-testid="create-announcement-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Megaphone className="h-5 w-5 text-indigo-500" />
                                New Announcement
                            </DialogTitle>
                            <DialogDescription>This message will appear as a modal on users' next login.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div>
                                <Label>Title</Label>
                                <Input
                                    value={announcementForm.title}
                                    onChange={e => setAnnouncementForm(f => ({ ...f, title: e.target.value }))}
                                    placeholder="e.g. New Feature: Study Plan Analytics"
                                    data-testid="announcement-title-input"
                                />
                            </div>
                            <div>
                                <Label>Message</Label>
                                <textarea
                                    value={announcementForm.body}
                                    onChange={e => setAnnouncementForm(f => ({ ...f, body: e.target.value }))}
                                    placeholder="Write your announcement message here..."
                                    rows={4}
                                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                                    data-testid="announcement-body-input"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="mb-1.5 block">Priority</Label>
                                    <div className="flex gap-1.5" data-testid="announcement-priority-select">
                                        {[
                                            { key: 'info', label: 'Info', icon: Info, color: 'blue' },
                                            { key: 'important', label: 'Important', icon: AlertTriangle, color: 'amber' },
                                            { key: 'critical', label: 'Critical', icon: AlertOctagon, color: 'red' },
                                        ].map(p => {
                                            const sel = announcementForm.priority === p.key;
                                            const PIcon = p.icon;
                                            return (
                                                <button key={p.key} type="button"
                                                    onClick={() => setAnnouncementForm(f => ({ ...f, priority: p.key }))}
                                                    className={cn("flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-xs font-medium border transition-all",
                                                        sel ? `bg-${p.color}-50 border-${p.color}-300 text-${p.color}-700` : "bg-white border-slate-200 text-slate-500"
                                                    )}
                                                    style={sel ? {
                                                        backgroundColor: p.color === 'blue' ? '#eff6ff' : p.color === 'amber' ? '#fffbeb' : '#fef2f2',
                                                        borderColor: p.color === 'blue' ? '#93c5fd' : p.color === 'amber' ? '#fcd34d' : '#fca5a5',
                                                        color: p.color === 'blue' ? '#1d4ed8' : p.color === 'amber' ? '#92400e' : '#991b1b',
                                                    } : {}}
                                                    data-testid={`announcement-priority-${p.key}`}
                                                >
                                                    <PIcon className="h-3 w-3" />{p.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div>
                                    <Label className="mb-1.5 block">Audience</Label>
                                    <select
                                        value={announcementForm.target}
                                        onChange={e => setAnnouncementForm(f => ({ ...f, target: e.target.value }))}
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                        data-testid="announcement-target-select"
                                    >
                                        <option value="all">All Users</option>
                                        <option value="professors">Professors Only</option>
                                        <option value="students">Students Only</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <Label>Expiration (optional)</Label>
                                <Input
                                    type="datetime-local"
                                    value={announcementForm.expires_at}
                                    onChange={e => setAnnouncementForm(f => ({ ...f, expires_at: e.target.value }))}
                                    data-testid="announcement-expires-input"
                                />
                                <p className="text-xs text-slate-400 mt-1">Leave empty for no expiration. Expired announcements won't show to users.</p>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowCreateAnnouncement(false)}>Cancel</Button>
                            <Button onClick={handleCreateAnnouncement} disabled={creatingAnnouncement} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="announcement-create-btn">
                                {creatingAnnouncement ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</> : <><Megaphone className="h-4 w-4 mr-2" />Send Announcement</>}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Suspend Confirmation Dialog */}
                <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, user: null, action: null })}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-amber-500" />
                                {confirmDialog.action === 'suspend' ? 'Suspend User' : 'Unsuspend User'}
                            </DialogTitle>
                            <DialogDescription>
                                {confirmDialog.action === 'suspend'
                                    ? `Suspend ${confirmDialog.user?.name}? They won't be able to access the platform.`
                                    : `Unsuspend ${confirmDialog.user?.name}? They will regain access.`}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, user: null, action: null })}>Cancel</Button>
                            <Button variant={confirmDialog.action === 'suspend' ? 'destructive' : 'default'}
                                onClick={() => handleSuspend(confirmDialog.user, confirmDialog.action === 'suspend')}
                                data-testid="admin-confirm-action">
                                {confirmDialog.action === 'suspend' ? 'Suspend' : 'Unsuspend'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Premium Status Dialog */}
                <Dialog open={premiumDialog.open} onOpenChange={(open) => !open && setPremiumDialog({ open: false, user: null })}>
                    <DialogContent data-testid="admin-premium-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Crown className="h-5 w-5 text-amber-500" />
                                Set Premium Status
                            </DialogTitle>
                            <DialogDescription>
                                Grant or revoke premium for <strong>{premiumDialog.user?.name}</strong> ({premiumDialog.user?.email})
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3 py-2">
                            <Label>Duration</Label>
                            <select value={premiumDays} onChange={(e) => setPremiumDays(Number(e.target.value))}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                data-testid="admin-premium-duration">
                                <option value={0}>Revoke Premium</option>
                                <option value={30}>1 Month (30 days)</option>
                                <option value={90}>3 Months</option>
                                <option value={180}>6 Months</option>
                                <option value={365}>1 Year</option>
                                <option value={730}>2 Years</option>
                                <option value={1095}>3 Years</option>
                            </select>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setPremiumDialog({ open: false, user: null })}>Cancel</Button>
                            <Button onClick={handleSetPremium} className={premiumDays > 0 ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""} variant={premiumDays <= 0 ? "destructive" : "default"}
                                data-testid="admin-premium-confirm">
                                {premiumDays > 0 ? <><Crown className="h-4 w-4 mr-1" />Grant Premium</> : <><ShieldOff className="h-4 w-4 mr-1" />Revoke</>}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Reset Password Dialog */}
                <Dialog open={resetDialog.open} onOpenChange={(open) => !open && setResetDialog({ open: false, user: null })}>
                    <DialogContent data-testid="admin-reset-pwd-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <KeyRound className="h-5 w-5 text-amber-500" />
                                Reset Password
                            </DialogTitle>
                            <DialogDescription>
                                Set a new password for <strong>{resetDialog.user?.name}</strong> ({resetDialog.user?.email})
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3 py-2">
                            <Label>New Password</Label>
                            <Input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password (min 6 chars)" data-testid="admin-reset-pwd-input" />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setResetDialog({ open: false, user: null })}>Cancel</Button>
                            <Button onClick={handleResetPassword} disabled={newPassword.length < 6} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="admin-reset-pwd-confirm">
                                <KeyRound className="h-4 w-4 mr-1" />Reset Password
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Reclassify Dialog */}
                <Dialog open={reclassifyDialog.open} onOpenChange={(open) => !open && setReclassifyDialog({ open: false, topic: '', oldCategory: '', oldGroup: '' })}>


                    <DialogContent data-testid="admin-reclassify-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <ArrowRight className="h-5 w-5 text-indigo-500" />
                                Reclassify Topic
                            </DialogTitle>
                            <DialogDescription>
                                Move "<strong>{reclassifyDialog.topic}</strong>" from {reclassifyDialog.oldGroup} to a new category.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3 py-2">
                            <div>
                                <Label>Target Type</Label>
                                <select value={reclassifyTarget.category} onChange={(e) => setReclassifyTarget({ category: e.target.value, group: '' })}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                                    <option value="systems">System</option>
                                    <option value="subjects">Subject</option>
                                </select>
                            </div>
                            <div>
                                <Label>Target Group</Label>
                                <select value={reclassifyTarget.group} onChange={(e) => setReclassifyTarget(prev => ({ ...prev, group: e.target.value }))}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    data-testid="admin-reclassify-target">
                                    <option value="">Select target...</option>
                                    {Object.keys(libraryData.static_topics[reclassifyTarget.category] || {}).map(g => (
                                        <option key={g} value={g}>{g}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setReclassifyDialog({ open: false, topic: '', oldCategory: '', oldGroup: '' })}>Cancel</Button>
                            <Button onClick={handleReclassify} disabled={!reclassifyTarget.group} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="admin-reclassify-confirm">
                                <ArrowRight className="h-4 w-4 mr-1" />Move Topic
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Bulk Premium Dialog */}
                <Dialog open={bulkPremiumDialog} onOpenChange={setBulkPremiumDialog}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Bulk Set Premium</DialogTitle>
                            <DialogDescription>
                                Grant premium access to <strong>{selectedUsers.size}</strong> selected user(s).
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-2">
                            <Label>Premium Duration (days)</Label>
                            <div className="flex gap-2 mt-1">
                                {[30, 90, 180, 365].map(d => (
                                    <Button key={d} size="sm" variant={bulkPremiumDays === d ? 'default' : 'outline'}
                                        onClick={() => setBulkPremiumDays(d)} data-testid={`bulk-premium-${d}d`}>
                                        {d}d
                                    </Button>
                                ))}
                                <Input type="number" value={bulkPremiumDays} onChange={(e) => setBulkPremiumDays(parseInt(e.target.value) || 0)}
                                    className="w-24" data-testid="bulk-premium-days-input" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setBulkPremiumDialog(false)}>Cancel</Button>
                            <Button onClick={handleBulkSetPremium} disabled={bulkActionLoading}
                                className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="bulk-premium-confirm">
                                {bulkActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Crown className="h-4 w-4 mr-1" />}
                                Grant Premium
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Bulk Delete Dialog */}
                <Dialog open={bulkDeleteDialog} onOpenChange={setBulkDeleteDialog}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="text-red-700">Delete Users</DialogTitle>
                            <DialogDescription>
                                This will permanently delete <strong>{selectedUsers.size}</strong> selected user(s) and all their data. This action cannot be undone.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setBulkDeleteDialog(false)}>Cancel</Button>
                            <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkActionLoading} data-testid="bulk-delete-confirm">
                                {bulkActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
                                Delete {selectedUsers.size} User(s)
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Single User Delete Dialog */}
                <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, user: null })}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="text-red-700">Delete User</DialogTitle>
                            <DialogDescription>
                                Permanently delete <strong>{deleteDialog.user?.email}</strong> and all their data (attempts, payments, membership). This cannot be undone.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, user: null })}>Cancel</Button>
                            <Button variant="destructive" onClick={handleDeleteUser} data-testid="admin-delete-confirm">
                                <Trash2 className="h-4 w-4 mr-1" />Delete User
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
